import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';

const ReportsView = () => {
  const { isDemo } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('todos');
  const [selectedTipo, setSelectedTipo] = useState('todos');
  const [selectedUbicacion, setSelectedUbicacion] = useState('todos');
  
  // Rango por defecto: últimos 7 días
  const getInitialDates = () => {
    const today = new Date();
    const endStr = today.toISOString().split('T')[0];
    const prev = new Date();
    prev.setDate(prev.getDate() - 7);
    const startStr = prev.toISOString().split('T')[0];
    return { startStr, endStr };
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.startStr);
  const [endDate, setEndDate] = useState(initialDates.endStr);

  const [checadas, setChecadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadInitialData();
  }, [isDemo]);

  const loadInitialData = async () => {
    if (isDemo) {
      setUsers([{ id: 'demo-user', nombre: 'Usuario Demo', email: 'demo@comarsa.com' }]);
      return;
    }
    try {
      const usersSnap = await getDocs(collection(db, 'usuarios'));
      const userList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(userList);
    } catch (error) {
      console.error('Error al cargar lista de usuarios:', error);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setMessage('');
    if (isDemo) {
      setChecadas([]);
      setMessage('ℹ️ Modo Demostración: No hay reportes históricos registrados para esta cuenta.');
      setLoading(false);
      return;
    }
    try {
      const start = new Date(`${startDate}T00:00:00`);
      const end = new Date(`${endDate}T23:59:59`);

      const q = query(
        collection(db, 'checadas'),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('timestamp', '<=', Timestamp.fromDate(end)),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      let results = snapshot.docs.map(doc => {
        const data = doc.data();
        const isFuera = data.fueraDeSucursal === true || (data.ubicacion && data.ubicacion.withinGeofence === false);
        return {
          id: doc.id,
          ...data,
          isFuera,
          timestamp: data.timestamp?.toDate()
        };
      });

      if (selectedUser !== 'todos') {
        results = results.filter(item => item.userId === selectedUser);
      }
      if (selectedTipo !== 'todos') {
        results = results.filter(item => item.tipo === selectedTipo);
      }
      if (selectedUbicacion === 'en_sucursal') {
        results = results.filter(item => !item.isFuera);
      } else if (selectedUbicacion === 'fuera_sucursal') {
        results = results.filter(item => item.isFuera);
      }

      setChecadas(results);
      if (results.length === 0) {
        setMessage('ℹ️ No se encontraron checadas con los filtros seleccionados.');
      }
    } catch (error) {
      console.error('Error al buscar checadas:', error);
      setMessage(`❌ Error al cargar reporte: ${error.message}`);
    }
    setLoading(false);
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? (u.nombre || u.email) : userId;
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      entrada: '🚪 Entrada',
      salida_comida: '🍽️ Salida a Comer',
      entrada_comida: '🔙 Regreso de Comer',
      salida: '🏠 Salida'
    };
    return tipos[tipo] || tipo;
  };

  const exportToCSV = () => {
    if (checadas.length === 0) return;

    const headers = ['ID Checada', 'Usuario', 'Email', 'Tipo', 'Fecha y Hora', 'Estado de Ubicación', 'Sucursal', 'Distancia (m)', 'Enlace a Mapa'];
    const rows = checadas.map(c => {
      const user = users.find(u => u.id === c.userId);
      const estadoUbicacion = c.isFuera ? '⚠️ FUERA DE SUCURSAL' : 'EN SUCURSAL';
      const sucursal = c.ubicacion?.sucursalNombre || 'Sucursal';
      const mapsLink = (c.ubicacion?.latitude && c.ubicacion?.longitude)
        ? `https://www.google.com/maps?q=${c.ubicacion.latitude},${c.ubicacion.longitude}`
        : '';
      return [
        `"${c.id}"`,
        `"${user?.nombre || c.userId || ''}"`,
        `"${user?.email || ''}"`,
        `"${c.tipo}"`,
        `"${c.timestamp ? c.timestamp.toLocaleString('es-MX') : ''}"`,
        `"${estadoUbicacion}"`,
        `"${sucursal}"`,
        `"${c.ubicacion?.distance !== undefined && c.ubicacion?.distance !== null ? c.ubicacion.distance : ''}"`,
        `"${mapsLink}"`
      ];
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_asistencias_${startDate}_al_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalEntradas = checadas.filter(c => c.tipo === 'entrada').length;
  const totalSalidas = checadas.filter(c => c.tipo === 'salida').length;
  const totalFueraSucursal = checadas.filter(c => c.isFuera).length;

  return (
    <div className="reports-view">
      <h3>📊 Reportes de Asistencia</h3>

      {/* Formulario de filtros */}
      <form onSubmit={handleSearch} className="form-section" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          <div className="field">
            <label>Fecha Inicio</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label>Fecha Fin</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label>Empleado</label>
            <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
              <option value="todos">Todos los empleados</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.nombre || u.email}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Tipo de Checada</label>
            <select value={selectedTipo} onChange={(e) => setSelectedTipo(e.target.value)}>
              <option value="todos">Todos los tipos</option>
              <option value="entrada">Entrada</option>
              <option value="salida_comida">Salida a Comer</option>
              <option value="entrada_comida">Regreso de Comer</option>
              <option value="salida">Salida</option>
            </select>
          </div>
          <div className="field">
            <label>Filtro de Ubicación</label>
            <select value={selectedUbicacion} onChange={(e) => setSelectedUbicacion(e.target.value)}>
              <option value="todos">Todas las checadas</option>
              <option value="en_sucursal">✅ Solo dentro de sucursal</option>
              <option value="fuera_sucursal">⚠️ Solo fuera de sucursal</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'Consultando...' : '🔍 Generar Reporte'}
          </button>
          {checadas.length > 0 && (
            <button type="button" onClick={exportToCSV} className="btn btn-success">
              📥 Exportar a Excel (CSV)
            </button>
          )}
        </div>
      </form>

      {/* Tarjetas resumen */}
      {checadas.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          <div className="panel-card" style={{ padding: '14px', textAlign: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Checadas</span>
            <h2 style={{ fontSize: '1.6rem', color: 'var(--primary)', margin: '4px 0 0' }}>{checadas.length}</h2>
          </div>
          <div className="panel-card" style={{ padding: '14px', textAlign: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Entradas</span>
            <h2 style={{ fontSize: '1.6rem', color: 'var(--success)', margin: '4px 0 0' }}>{totalEntradas}</h2>
          </div>
          <div className="panel-card" style={{ padding: '14px', textAlign: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Salidas</span>
            <h2 style={{ fontSize: '1.6rem', color: 'var(--danger)', margin: '4px 0 0' }}>{totalSalidas}</h2>
          </div>
          <div className="panel-card" style={{ padding: '14px', textAlign: 'center', borderColor: totalFueraSucursal > 0 ? '#fde68a' : 'var(--border)' }}>
            <span style={{ fontSize: '0.75rem', color: totalFueraSucursal > 0 ? '#b45309' : 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
              ⚠️ Fuera de Sucursal
            </span>
            <h2 style={{ fontSize: '1.6rem', color: totalFueraSucursal > 0 ? '#b45309' : 'var(--text)', margin: '4px 0 0' }}>
              {totalFueraSucursal}
            </h2>
          </div>
        </div>
      )}

      {message && <div className="message">{message}</div>}

      {/* Tabla de resultados */}
      {checadas.length > 0 && (
        <div className="table-wrapper">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Empleado</th>
                <th>Fecha y Hora</th>
                <th>Ubicación / Sucursal</th>
              </tr>
            </thead>
            <tbody>
              {checadas.map((c) => (
                <tr key={c.id}>
                  <td>{getTipoLabel(c.tipo)}</td>
                  <td>
                    <strong>{getUserName(c.userId)}</strong>
                  </td>
                  <td>{c.timestamp ? c.timestamp.toLocaleString('es-MX') : 'Sin fecha'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {c.isFuera ? (
                        <span className="badge badge-warning" style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', fontWeight: 600 }}>
                          ⚠️ Fuera de sucursal ({c.ubicacion?.distance !== undefined && c.ubicacion?.distance !== null ? `${c.ubicacion.distance}m` : 'Sin GPS'})
                        </span>
                      ) : (
                        <span className="badge badge-success">
                          ✅ {c.ubicacion?.sucursalNombre || 'En sucursal'} ({c.ubicacion?.distance ?? 0}m)
                        </span>
                      )}

                      {c.ubicacion?.latitude && c.ubicacion?.longitude && (
                        <a
                          href={`https://www.google.com/maps?q=${c.ubicacion.latitude},${c.ubicacion.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Ver en Google Maps"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            background: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textDecoration: 'none'
                          }}
                        >
                          <i className="fa-solid fa-map-location-dot"></i> Maps
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportsView;